import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Calculator, RefreshCw, Lock, CheckCircle, AlertTriangle, PlusCircle } from 'lucide-react';

interface PrepaidPlanSectionProps {
    userId: string;
    planInfo: any;
    onPlanUpdated: () => void;
}

interface PrepaidData {
    exists: boolean;
    balance_amount?: number;
    price_per_message?: number;
    message_limit?: number;
    usage?: number;
    remaining?: number;
    status?: string;
    activated_at?: string;
    is_exhausted?: boolean;
}

const PRICE_PER_MESSAGE = 95;

/**
 * PrepaidPlanSection - Component for managing prepaid plans.
 *
 * Three distinct actions:
 * 1. "Nuevo Plan Prepago" (PUT)  → closes current plan, starts usage from 0
 * 2. "Modificar Saldo"   (PATCH) → updates balance/limit only, keeps usage counter
 * 3. "Bloquear"          (POST)  → blocks the account
 */
export const PrepaidPlanSection: React.FC<PrepaidPlanSectionProps> = ({
    userId,
    planInfo,
    onPlanUpdated,
}) => {
    const [prepaidData, setPrepaidData] = useState<PrepaidData | null>(null);
    const [loading, setLoading] = useState(false);
    const [balanceInput, setBalanceInput] = useState<string>('');
    const [calculatedMessages, setCalculatedMessages] = useState<number | null>(null);

    // 'new'    → "Nuevo Plan Prepago" (PUT, resets from 0)
    // 'modify' → "Modificar Saldo"   (PATCH, keeps usage)
    // null     → not editing
    const [editMode, setEditMode] = useState<'new' | 'modify' | null>(null);

    // Modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'create' | 'new' | 'modify' | 'block'>('create');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchPrepaidData();
    }, [userId]);

    const fetchPrepaidData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/admin/users/${userId}/prepaid`);
            setPrepaidData(res.data);
        } catch (err) {
            console.error('Error fetching prepaid data:', err);
            setPrepaidData({ exists: false });
        } finally {
            setLoading(false);
        }
    };

    const handleBalanceChange = (value: string) => {
        const cleaned = value.replace(/[^0-9.]/g, '');
        setBalanceInput(cleaned);
        const numVal = parseFloat(cleaned);
        if (!isNaN(numVal) && numVal > 0) {
            setCalculatedMessages(Math.ceil(numVal / PRICE_PER_MESSAGE));
        } else {
            setCalculatedMessages(null);
        }
    };

    const formatNumber = (num: number): string => num.toLocaleString('es-CO');

    const openEdit = (mode: 'new' | 'modify') => {
        setEditMode(mode);
        setBalanceInput('');
        setCalculatedMessages(null);
    };

    const cancelEdit = () => {
        setEditMode(null);
        setBalanceInput('');
        setCalculatedMessages(null);
    };

    const handleShowConfirm = (action: 'create' | 'new' | 'modify' | 'block') => {
        setConfirmAction(action);
        setShowConfirmModal(true);
    };

    const handleConfirm = async () => {
        setActionLoading(true);
        try {
            if (confirmAction === 'create') {
                // First-time creation (no existing plan)
                await axios.post(`/admin/users/${userId}/prepaid`, {
                    balance_amount: parseFloat(balanceInput),
                });
            } else if (confirmAction === 'new') {
                // New plan — resets usage to 0 (PUT)
                await axios.put(`/admin/users/${userId}/prepaid`, {
                    balance_amount: parseFloat(balanceInput),
                });
            } else if (confirmAction === 'modify') {
                // Modify balance/limit only — keeps usage (PATCH)
                await axios.patch(`/admin/users/${userId}/prepaid`, {
                    balance_amount: parseFloat(balanceInput),
                });
            } else if (confirmAction === 'block') {
                await axios.post(`/admin/users/${userId}/prepaid/block`);
            }

            setShowConfirmModal(false);
            setBalanceInput('');
            setCalculatedMessages(null);
            setEditMode(null);
            await fetchPrepaidData();
            onPlanUpdated();
        } catch (err: any) {
            console.error('Error executing prepaid action:', err);
            alert(err?.response?.data?.detail || 'Error al procesar la solicitud');
        } finally {
            setActionLoading(false);
        }
    };

    const hasPlan = prepaidData?.exists && prepaidData.status !== 'blocked';
    const isExhausted = prepaidData?.is_exhausted;

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">

            {/* ── Active Prepaid Plan Info ── */}
            {hasPlan && !editMode && (
                <div className={`rounded-xl p-5 border ${isExhausted ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <DollarSign className={`h-5 w-5 ${isExhausted ? 'text-red-600' : 'text-emerald-600'}`} />
                        <h4 className="font-semibold text-gray-900">Plan Prepago</h4>
                        <span className={`ml-auto px-3 py-1 text-xs font-semibold rounded-full ${isExhausted ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {isExhausted ? 'Agotado' : 'Activo'}
                        </span>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-sm mb-4">
                        <div className="bg-white rounded-lg p-2 border">
                            <div className="font-bold text-lg text-emerald-600">
                                ${formatNumber(prepaidData?.balance_amount || 0)}
                            </div>
                            <div className="text-gray-500 text-xs">Saldo Recargado</div>
                        </div>
                        <div className="bg-white rounded-lg p-2 border">
                            <div className="font-bold text-lg text-green-600">{prepaidData?.usage || 0}</div>
                            <div className="text-gray-500 text-xs">Usados</div>
                        </div>
                        <div className="bg-white rounded-lg p-2 border">
                            <div className={`font-bold text-lg ${(prepaidData?.remaining || 0) <= 0 ? 'text-red-600' : 'text-indigo-600'}`}>
                                {prepaidData?.remaining || 0}
                            </div>
                            <div className="text-gray-500 text-xs">Disponibles</div>
                        </div>
                        <div className="bg-white rounded-lg p-2 border">
                            <div className="font-bold text-lg text-gray-700">{prepaidData?.message_limit || 0}</div>
                            <div className="text-gray-500 text-xs">Límite</div>
                        </div>
                    </div>

                    {/* Usage Progress Bar */}
                    {(prepaidData?.message_limit || 0) > 0 && (
                        <div className="mt-2 mb-4">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Uso del plan</span>
                                <span className="font-medium">
                                    {Math.round(((prepaidData?.usage || 0) / (prepaidData?.message_limit || 1)) * 100)}%
                                </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all ${(prepaidData?.remaining || 0) <= 0
                                        ? 'bg-red-500'
                                        : (prepaidData?.remaining || 0) < ((prepaidData?.message_limit || 0) * 0.2)
                                            ? 'bg-yellow-500'
                                            : 'bg-green-500'
                                        }`}
                                    style={{
                                        width: `${Math.min(100, ((prepaidData?.usage || 0) / (prepaidData?.message_limit || 1)) * 100)}%`,
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Plan Details */}
                    <div className="bg-white/60 rounded-lg p-3 mb-4">
                        <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex justify-between">
                                <span>Precio por mensaje:</span>
                                <span className="font-medium">${PRICE_PER_MESSAGE} COP</span>
                            </div>
                            {prepaidData?.activated_at && (
                                <div className="flex justify-between">
                                    <span>Activado:</span>
                                    <span className="font-medium">
                                        {new Date(prepaidData.activated_at).toLocaleString('es-ES', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {isExhausted && (
                        <p className="text-red-600 text-sm font-medium mb-3">
                            ⚠️ El saldo prepago se ha agotado. La cuenta está bloqueada para envíos.
                        </p>
                    )}

                    {/* Action Buttons — 3 equal buttons */}
                    <div className="grid grid-cols-3 gap-2">
                        {/* Nuevo Plan (resets counter) — Rebeca teal */}
                        <button
                            onClick={() => openEdit('new')}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 text-white text-sm font-medium rounded-lg transition-colors"
                            style={{ backgroundColor: '#1CB3B3' }}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#17a0a0')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#1CB3B3')}
                        >
                            <PlusCircle className="h-4 w-4" />
                            Nuevo Plan
                        </button>

                        {/* Modificar Saldo (keeps counter) */}
                        <button
                            onClick={() => openEdit('modify')}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Modificar Saldo
                        </button>

                        {/* Bloquear */}
                        <button
                            onClick={() => handleShowConfirm('block')}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                        >
                            <Lock className="h-4 w-4" />
                            Bloquear
                        </button>
                    </div>
                </div>
            )}

            {/* ── Create / Edit Form ── */}
            {(!hasPlan || editMode) && (
                <div className="rounded-xl p-5 border bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">
                            {!hasPlan
                                ? 'Crear Plan Prepago'
                                : editMode === 'new'
                                    ? 'Nuevo Plan Prepago'
                                    : 'Modificar Saldo'}
                        </h4>
                    </div>

                    {/* Contextual explanation */}
                    {editMode === 'new' && (
                        <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 mb-3">
                            🔄 <strong>Nuevo Plan:</strong> Se cierra el plan actual y empieza uno nuevo desde 0 mensajes enviados.
                        </p>
                    )}
                    {editMode === 'modify' && (
                        <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-3">
                            ✏️ <strong>Modificar Saldo:</strong> Solo se actualiza el límite de mensajes. Los mensajes ya enviados se mantienen.
                        </p>
                    )}

                    <div className="space-y-4">
                        {/* Balance Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Monto del Saldo (COP)
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                <input
                                    type="text"
                                    value={balanceInput}
                                    onChange={(e) => handleBalanceChange(e.target.value)}
                                    placeholder="Ej: 40000"
                                    className="w-full pl-7 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Calculation Result */}
                        {calculatedMessages !== null && (
                            <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Calculator className="h-4 w-4 text-indigo-600" />
                                    <span className="text-sm font-semibold text-indigo-900">Cálculo</span>
                                </div>
                                <div className="text-xs text-gray-600 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Saldo ingresado:</span>
                                        <span className="font-medium">${formatNumber(parseFloat(balanceInput))} COP</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Precio por mensaje:</span>
                                        <span className="font-medium">${PRICE_PER_MESSAGE} COP</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Cálculo:</span>
                                        <span className="font-medium">
                                            {formatNumber(parseFloat(balanceInput))} ÷ {PRICE_PER_MESSAGE} = {(parseFloat(balanceInput) / PRICE_PER_MESSAGE).toFixed(2)}
                                        </span>
                                    </div>
                                    <hr className="my-2 border-indigo-100" />
                                    <div className="flex justify-between text-sm">
                                        <span className="font-semibold text-indigo-900">Mensajes disponibles:</span>
                                        <span className="font-bold text-indigo-600 text-lg">{formatNumber(calculatedMessages)}</span>
                                    </div>
                                    {editMode === 'modify' && prepaidData?.usage !== undefined && (
                                        <div className="flex justify-between text-sm mt-1 pt-1 border-t border-indigo-100">
                                            <span className="text-gray-600">Ya enviados (se mantienen):</span>
                                            <span className="font-semibold text-gray-700">{prepaidData.usage}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                            {editMode && (
                                <button
                                    onClick={cancelEdit}
                                    className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Cancelar
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    if (!hasPlan) handleShowConfirm('create');
                                    else if (editMode === 'new') handleShowConfirm('new');
                                    else handleShowConfirm('modify');
                                }}
                                disabled={!calculatedMessages}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${calculatedMessages
                                    ? editMode === 'new'
                                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                <CheckCircle className="h-4 w-4" />
                                {!hasPlan ? 'Crear Plan' : editMode === 'new' ? 'Confirmar Nuevo Plan' : 'Guardar Cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirmation Modal ── */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/50" onClick={() => !actionLoading && setShowConfirmModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`p-2 rounded-full ${confirmAction === 'block' ? 'bg-red-100' : confirmAction === 'new' ? 'bg-teal-100' : 'bg-indigo-100'}`}>
                                {confirmAction === 'block' ? (
                                    <AlertTriangle className="h-6 w-6 text-red-600" />
                                ) : confirmAction === 'new' ? (
                                    <PlusCircle className="h-6 w-6 text-teal-600" />
                                ) : (
                                    <CheckCircle className="h-6 w-6 text-indigo-600" />
                                )}
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {confirmAction === 'block'
                                    ? '¿Bloquear cuenta?'
                                    : confirmAction === 'new'
                                        ? '¿Crear nuevo plan prepago?'
                                        : confirmAction === 'modify'
                                            ? '¿Modificar saldo del plan?'
                                            : '¿Crear plan prepago?'}
                            </h3>
                        </div>

                        <p className="text-sm text-gray-600 mb-6">
                            {confirmAction === 'block' ? (
                                '¿Estás seguro que deseas bloquear esta cuenta? La cuenta no podrá enviar más mensajes.'
                            ) : confirmAction === 'new' ? (
                                <>
                                    El plan actual se cerrará y se creará uno nuevo desde <strong className="text-violet-700">0 mensajes enviados</strong>. El nuevo límite será{' '}
                                    <strong className="text-violet-700">{formatNumber(calculatedMessages || 0)} mensajes</strong>.
                                </>
                            ) : confirmAction === 'modify' ? (
                                <>
                                    Solo se actualizará el límite a <strong className="text-indigo-700">{formatNumber(calculatedMessages || 0)} mensajes</strong>. Los{' '}
                                    <strong className="text-gray-700">{prepaidData?.usage || 0} mensajes</strong> ya enviados se mantienen.
                                </>
                            ) : (
                                <>
                                    ¿Estás seguro que deseas crear un plan con{' '}
                                    <strong className="text-indigo-700">{formatNumber(calculatedMessages || 0)} mensajes</strong>?
                                </>
                            )}
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                disabled={actionLoading}
                                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={actionLoading}
                                className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${confirmAction === 'block'
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : confirmAction === 'new'
                                        ? 'bg-teal-600 hover:bg-teal-700'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                            >
                                {actionLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                ) : (
                                    'Confirmar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrepaidPlanSection;
